import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";

const SLIDES = [
  {
    headline1: "Let AI Find the",
    headline2: "Best Vendor for You",
    subtext: "Smart matching based on your business needs. Get personalized vendor recommendations instantly.",
    cta: "Get Started",
    ctaAction: "contact",
    image: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&q=80",
    imageAlt: "Technology and innovation",
  },
  {
    headline1: "Struggling with your",
    headline2: "Current Vendor?",
    subtext: "Switch to better, verified vendors effortlessly. No disruption, no hassle — just results.",
    cta: "Switch Now",
    ctaAction: "contact",
    image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80",
    imageAlt: "Business meeting discussion",
  },
  {
    headline1: "No Upfront Cost.",
    headline2: "Pay After Service.",
    subtext: "Compliance, IT, Supplies, Manpower — all in one place. Zero risk, maximum value.",
    cta: "Explore Services",
    ctaAction: "services",
    image: "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&q=80",
    imageAlt: "Modern office workspace",
  },
  {
    headline1: "Stop Settling,",
    headline2: "Start Switching",
    subtext: "From compliance to supplies — one partner, zero hassle. Replace your vendors at your convenience.",
    cta: "Explore Services",
    ctaAction: "services",
    image: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800&q=80",
    imageAlt: "Business team collaborating",
  },
];

export function HeroSection({ onOpenAppointment }) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => {
    setCurrent((c) => (c + 1) % SLIDES.length);
  }, []);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(next, 3500);
    return () => clearInterval(timer);
  }, [next, paused]);

  const slide = SLIDES[current];

  const handleCTA = (action) => {
    if (action === "services") {
      document.getElementById("services")?.scrollIntoView({ behavior: "smooth" });
    } else {
      document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      data-testid="hero-section"
      className="pt-20 pb-14 md:pt-24 md:pb-20 relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #fff5f5 0%, #ffffff 45%, #f0f4ff 100%)" }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Decorative circles */}
      <div className="absolute top-1/2 -right-32 w-[500px] h-[500px] rounded-full border border-red-100 -translate-y-1/2 pointer-events-none" />
      <div className="absolute top-1/2 -right-16 w-[300px] h-[300px] rounded-full border border-red-200/40 -translate-y-1/2 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        {/* Left - Text Content */}
        <div className="flex flex-col gap-4">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 rounded-full px-4 py-1.5 w-fit">
            <span className="w-2 h-2 bg-red-600 rounded-full animate-pulse-dot" />
            <span className="text-red-700 font-bold text-xs font-body">
              India's #1 Vendor Switching Platform
            </span>
          </div>

          {/* Animated slide content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              className="flex flex-col gap-4"
            >
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
                {slide.headline1}
                <br />
                <span className="bg-gradient-to-r from-red-600 to-rose-500 bg-clip-text text-transparent">
                  {slide.headline2}
                </span>
              </h1>

              <p className="text-slate-500 text-base md:text-lg leading-relaxed max-w-md font-body">
                {slide.subtext}
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  data-testid="hero-cta-primary"
                  onClick={() => handleCTA(slide.ctaAction)}
                  className="bg-gradient-to-r from-red-600 to-red-700 text-white font-extrabold px-7 py-3.5 rounded-2xl shadow-xl shadow-red-600/25 hover:shadow-red-600/35 transition-all active:scale-95 flex items-center justify-center gap-2 text-[15px] font-body"
                >
                  {slide.cta} <ArrowRight size={18} />
                </button>
                <button
                  data-testid="hero-book-appointment-btn"
                  onClick={onOpenAppointment}
                  className="border-2 border-slate-200 text-slate-700 font-extrabold px-7 py-3.5 rounded-2xl hover:border-red-200 hover:text-red-600 transition-all active:scale-95 text-[15px] font-body bg-white"
                >
                  Book Your Appointment
                </button>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Stats */}
          <div className="flex gap-8 pt-1">
            {[["500+", "Happy Clients"], ["10+", "Services"], ["30%", "Cost Savings"]].map(([n, l]) => (
              <div key={l}>
                <p className="text-2xl font-black text-slate-900 font-body">{n}</p>
                <p className="text-xs text-slate-400 font-semibold mt-0.5 font-body">{l}</p>
              </div>
            ))}
          </div>

          {/* Dots */}
          <div className="flex gap-2 pt-1" data-testid="hero-slider-dots">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                data-testid={`hero-dot-${i}`}
                onClick={() => setCurrent(i)}
                className="transition-all duration-300 rounded-full"
                style={{
                  width: current === i ? 28 : 10,
                  height: 10,
                  background: current === i
                    ? "linear-gradient(135deg, #dc2626, #e11d48)"
                    : "#e2e8f0",
                }}
              />
            ))}
          </div>
        </div>

        {/* Right - Image */}
        <div className="relative hidden lg:block">
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.5 }}
              className="rounded-3xl overflow-hidden shadow-2xl"
            >
              <img
                src={slide.image}
                alt={slide.imageAlt}
                className="w-full h-[400px] object-cover"
              />
            </motion.div>
          </AnimatePresence>

          {/* Floating badges */}
          <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-3.5 shadow-xl border border-slate-100">
            <p className="font-black text-2xl text-slate-900 font-body">500+</p>
            <p className="text-[13px] text-slate-500 font-semibold font-body">Happy Clients</p>
          </div>
          <div className="absolute -top-4 -right-4 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl p-3.5 shadow-xl shadow-red-600/40">
            <div className="flex items-center gap-1.5">
              <Sparkles size={16} className="text-yellow-300" />
              <p className="font-black text-lg text-white font-body">4.9/5</p>
            </div>
            <p className="text-[11px] text-red-200 font-semibold font-body">Client Rating</p>
          </div>
        </div>
      </div>
    </section>
  );
}
